/**
 * board/board-move-input.js
 *
 * Self-contained chessboard move-input widget.
 * Renders a small playable board in a floating panel anchored below a given
 * element. As moves are played the caller receives a formatted SAN sequence
 * string (e.g. "1.e4 c5 2.c3") identical to what the search servlet expects.
 *
 * ─── VERIFY BEFORE USE ───────────────────────────────────────────────────────
 * INPUT_EVENT_TYPE and COLOR are used here for the first time in the project.
 * Run from /frontend/src:
 *   node -e "const c=require('./node_modules/cm-chessboard/src/Chessboard.js');console.log(Object.keys(c))"
 * Both names must appear in the output. If they don't, adjust the import below.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Usage (Phase B wires this up to the search bar):
 *
 *   const bmi = new BoardMoveInput({
 *       onMovesChange: (seq) => searchInput.value = seq,
 *       onSubmit:      (seq) => runSearch(seq),
 *       onCancel:      ()    => restorePreviousInput(),
 *   });
 *
 *   boardIconBtn.addEventListener('click', () => {
 *       bmi.open(boardIconBtn, searchInput.value.trim() || null);
 *   });
 *
 *   searchBtn.addEventListener('click', () => {
 *       if (bmi.isOpen()) bmi.submit();
 *   });
 */

import { Chess } from 'chess.js';
import { positionIdentity, positionContext, validatePositionFen } from './position-identity.js';

// ── ⚠️  IMPORT VERIFICATION POINT ─────────────────────────────────────────────
// cm-chessboard v8 exports INPUT_EVENT_TYPE and COLOR from the main module.
// If the build fails here, check the version with:
//   cat node_modules/cm-chessboard/package.json | grep '"version"'
// For older versions the path might be:
//   import { INPUT_EVENT_TYPE, COLOR } from 'cm-chessboard/src/Chessboard.js'
import { Chessboard, INPUT_EVENT_TYPE, COLOR } from 'cm-chessboard';
import { Markers } from 'cm-chessboard/src/extensions/markers/Markers.js';
import { Position, FEN } from 'cm-chessboard/src/model/Position.js';

// ─────────────────────────────────────────────────────────────────────────────

const START_FEN = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';
const BODY_ACTIVE_CLASS = 'fc-bmiMoveInputActive';

// Same constant as FelixChessBoardController — single source of truth would be
// a shared config module, but keeping it mirrored here avoids a coupling dep.
const ASSETS_URL = '/etc/vendor-libraries/felixchess-versioned-library/versions/0.0.1/assets/';

// Marker definitions (re-use the same visual language as the main board viewer)
const MARKER_LAST_FROM = { class: 'fc-bmi__marker--lastFrom', slice: 'markerSquare' };
const MARKER_LAST_TO   = { class: 'fc-bmi__marker--lastTo',   slice: 'markerSquare' };
const MARKER_SELECTED  = { class: 'fc-bmi__marker--selected',  slice: 'markerSquare' };
const EDITOR_PIECES = [['k', 'king'], ['q', 'queen'], ['r', 'rook'], ['b', 'bishop'], ['n', 'knight'], ['p', 'pawn']];

export class BoardMoveInput {

    /**
     * @param {object}   options
     * @param {function} options.onMovesChange  Called with formatted SAN sequence after every move/undo.
     * @param {function} options.onSubmit       Called with final SAN sequence when user submits.
     * @param {function} options.onCancel       Called (no args) when user cancels / presses Escape.
     * @param {boolean}  options.enableModeSelector Enable the GA Moves / Board Editor modes.
     */
    constructor(options = {}) {
        this._onMovesChange = typeof options.onMovesChange === 'function' ? options.onMovesChange : null;
        this._onSubmit      = typeof options.onSubmit      === 'function' ? options.onSubmit      : null;
        this._onCancel      = typeof options.onCancel      === 'function' ? options.onCancel      : null;
        this._onModeChange = options.onModeChange || null;
        this._onEditorChange = options.onEditorChange || null;
        this._onClose = options.onClose || null;
        this._isSearchTarget = options.isSearchTarget || null;
        this._soundController = options.soundController || window.FelixChess?.BoardSounds || null;
        this._enableModeSelector = options.enableModeSelector === true;
        this._mode = 'moves';
        this._searchDisabled = false;
        this._anchorEl = null;
        this._editorPosition = null;
        this._editorResetFen = null;
        this._editorCleared = false;
        this._editorOrientation = COLOR.white;
        this._editorTool = 'reposition';
        this._editorFocusSquare = 'a1';
        this._inputGeneration = 0;

        this._chess    = null;
        this._board    = null;
        this._panelEl  = null;
        this._boardEl  = null;
        this._undoBtn  = null;
        this._selectedSquare = null;
        this._suppressNextDestinationClick = false;

        // Bound references kept for clean removeEventListener
        this._boundOutsideClick = this._onOutsideClick.bind(this);
        this._boundKeyDown      = this._onKeyDown.bind(this);
        this._boundBoardClick   = this._onBoardClick.bind(this);
        this._boundBoardKeyDown = this._onEditorKeyDown.bind(this);
        this._boundResize = () => {
            if (this._panelEl && this._anchorEl) {
                this._positionPanel(this._panelEl, this._anchorEl);
            }
        };
    }

    // ── Public API ────────────────────────────────────────────────────────────

    /**
     * Open the move-input panel anchored below anchorEl.
     * If already open, reload Moves; an editor handoff replaces the panel.
     *
     * @param {HTMLElement} anchorEl       The element to position the panel below.
     * @param {object} options Optional initialMode / initialFen; Reset restores the snapshot.
     * @param {string|null} moveSequence   Optional existing move sequence to pre-load
     *                                     ("1.e4 c5" / "e4 c5" / null → starting position).
     */
    open(anchorEl, moveSequence = null, { initialMode = 'moves', initialFen = null, variant = 'standard', spid = '', backrank = '' } = {}) {
        // Validate the detached snapshot before replacing an existing panel.
        const handoff = this._enableModeSelector && initialMode === 'board-editor';
        if (handoff) {
            initialFen = String(initialFen || '').trim().replace(/\s+/g, ' ');
            if (initialFen.split(' ').length === 4) initialFen += ' 0 1';
            if (!validatePositionFen(initialFen, { variant, spid, backrank }).ok) return false;
        }
        if (handoff && this._panelEl) this.close();
        if (this._panelEl) {
            // Already open — reload position from new sequence
            this._loadSequence(moveSequence);
            if (this._mode === 'moves') this._syncBoard();
            return;
        }

        const namespace = positionContext({ variant, spid, backrank });
        this._editorContext = handoff ? { variant: namespace.variant, spid: namespace.spid, backrank } : { variant: 'standard', spid: '', backrank: '' };
        this._editorResetFen = handoff ? initialFen : FEN.start;
        document.body.classList.add(BODY_ACTIVE_CLASS);
        this._mode = 'moves';
        this._anchorEl = anchorEl;
        this._createChessInstance(moveSequence);
        this._createPanel(anchorEl);
        if (handoff) {
            this._setMode('board-editor');
            this._panelEl.querySelector('[data-bmi-fen]')?.focus();
        }
        window.addEventListener('resize', this._boundResize);

        // ── Double rAF before initialising the board ───────────────────────
        // The panel is freshly appended but may have zero clientWidth during
        // the current layout pass. Two rAFs guarantee the browser has completed
        // at least one full layout cycle so cm-chessboard reads real dimensions.
        // This is the same pattern used throughout the project to prevent the
        // SVG from locking to 0×0. Do NOT remove the double rAF.
        const panel = this._panelEl;
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                if (this._panelEl !== panel) return;
                this._createBoard();
                this._enableMoveInput();
                this._updateUndoBtn();
                if (this._mode === 'moves') this._highlightLastMove();
                else this._syncEditorBoard();
            });
        });

        // Register global dismiss listeners (with a tick delay so the opening
        // click that triggered open() doesn't immediately re-close the panel)
        setTimeout(() => {
            if (this._panelEl !== panel) return;
            document.addEventListener('mousedown', this._boundOutsideClick);
            document.addEventListener('keydown',   this._boundKeyDown);
        }, 0);
    }

    /** Programmatically submit (search icon / Enter key from search bar). */
    submit() {
        const position = this.getEditorState();
        if (position && !position.searchable) return;
        const seq = this._getMoveSequence();
        if (this._onSubmit) {
            if (this._onSubmit(seq, position) === false) return;
        }
        this.close({ submitted: true });
    }

    showSearchMessage({ title, message }) {
        const notification = this._panelEl?.querySelector('[data-bmi-search-message]');
        if (!notification) return;
        notification.hidden = false;
        this._panelEl.querySelector('[data-bmi-search-title]').textContent = title;
        this._panelEl.querySelector('[data-bmi-search-body]').textContent = message;
        this._panelEl.querySelector('[data-bmi-continue]').focus();
    }

    /** Mirror the host search action's eligibility, including an active request. */
    setSearchDisabled(disabled) {
        this._searchDisabled = Boolean(disabled);
        this._syncEditorSearch();
    }

    _syncEditorSearch() {
        const button = this._panelEl?.querySelector('[data-bmi-search]');
        if (!button) return;
        button.hidden = this._mode !== 'board-editor';
        button.disabled = this._searchDisabled || !this.getEditorState()?.searchable;
        button.setAttribute('aria-disabled', String(button.disabled));
    }

    /** Programmatically cancel (Escape / click outside). */
    cancel() {
        if (this._onCancel) {
            this._onCancel();
        }
        this.close();
    }

    /** Undo the last move played on the board. */
    undo() {
        if (this._mode !== 'moves' || !this._chess || !this._board) return;

        const undone = this._chess.undo();
        if (!undone) return;

        // Disable before repositioning so we don't fire stale input events
        this._board.disableMoveInput();
        this._board.setPosition(this._chess.fen(), false);
        this._enableMoveInput();
        this._updateUndoBtn();
        this._highlightLastMove();

        if (this._onMovesChange) {
            this._onMovesChange(this._getMoveSequence());
        }
    }

    /** Tear down the panel and release all resources. */
    close({ submitted = false } = {}) {
        const mode = this._mode;
        const wasOpen = !!this._panelEl;
        this._clearSelectedMarker();
        document.body.classList.remove(BODY_ACTIVE_CLASS);
        document.removeEventListener('mousedown', this._boundOutsideClick);
        document.removeEventListener('keydown',   this._boundKeyDown);
        window.removeEventListener('resize', this._boundResize);
        if (this._boardEl) {
            this._boardEl.removeEventListener('click', this._boundBoardClick);
            this._boardEl.removeEventListener('keydown', this._boundBoardKeyDown);
        }

        if (this._board) {
            try { this._board.destroy(); } catch (_) { /* ignore */ }
            this._board = null;
        }

        if (this._panelEl && this._panelEl.parentNode) {
            this._panelEl.parentNode.removeChild(this._panelEl);
        }

        this._panelEl = null;
        this._boardEl = null;
        this._undoBtn = null;
        this._chess   = null;
        this._selectedSquare = null;
        this._suppressNextDestinationClick = false;
        this._mode = 'moves';
        this._anchorEl = null;
        this._editorPosition = null;
        this._editorCleared = false;
        this._editorTool = 'reposition';
        this._editorMetadata = null;
        this._editorResetFen = null;
        this._fenError = null;
        this._orientationTask = null;
        this._inputGeneration++;
        if (wasOpen) this._onClose?.({ mode, submitted });
    }

    /** @returns {boolean} Whether the panel is currently visible. */
    isOpen() {
        return !!this._panelEl;
    }

    /** Full teardown — call if the parent component is destroyed. */
    destroy() {
        this.close();
        this._onMovesChange = null;
        this._onSubmit      = null;
        this._onCancel      = null;
        this._onModeChange = this._onEditorChange = this._onClose = null;
    }

    // ── Private: chess.js ─────────────────────────────────────────────────────

    /**
     * Build a chess.js instance pre-loaded with moveSequence.
     * Accepts all formats the search box accepts: "1.e4 c5 2.c3", "e4 c5 c3", etc.
     * Silently falls back to the starting position if the sequence is unparseable.
     */
    _createChessInstance(moveSequence) {
        this._chess = new Chess();
        this._loadSequence(moveSequence);
    }

    _loadSequence(moveSequence) {
        if (!this._chess) return;

        this._chess.reset();

        if (!moveSequence || !moveSequence.trim()) return;

        const raw = moveSequence.trim();

        // Strategy 1: loadPgn handles "1.e4 c5 2.c3", "1. e4 c5" etc.
        try {
            this._chess.loadPgn(raw);
            return;
        } catch (_) { /* fall through */ }

        // Strategy 2: strip move numbers, replay token by token.
        // Handles bare "e4 c5 Nf3" without numbers.
        this._chess.reset();
        const tokens = raw.split(/\s+/).filter(t => t && !/^\d+\.+$/.test(t));
        for (const san of tokens) {
            let result;
            try { result = this._chess.move(san, { sloppy: true }); }
            catch (_) { /* Prose and invalid suffixes are not legal move input. */ }
            if (!result) {
                // Invalid token — stop here, accept however many moves succeeded
                console.warn('BoardMoveInput: stopped pre-loading at invalid move token:', san);
                break;
            }
        }
    }

    // ── Private: DOM panel ────────────────────────────────────────────────────

    _createPanel(anchorEl) {
        const panel = document.createElement('div');
        panel.className = 'fc-bmi__panel';
        if (this._enableModeSelector) panel.classList.add('fc-bmi__panel--modes');
        panel.setAttribute('role', 'dialog');
        panel.setAttribute('aria-modal', 'false');
        panel.setAttribute('aria-label', 'Enter moves on chessboard');

        panel.innerHTML = `
            ${this._enableModeSelector ? `
            <div class="fc-bmi__modes" role="group" aria-label="Board mode">
                <button type="button" class="fc-bmi__modeBtn" data-bmi-mode="moves"
                        aria-pressed="true" title="Play moves to build a position.">Moves</button>
                <button type="button" class="fc-bmi__modeBtn" data-bmi-mode="board-editor"
                        aria-pressed="false" title="Create a custom position.">Board Editor</button>
            </div>` : ''}
            <div class="fc-bmi__board" data-bmi-board></div>
            <div class="fc-bmi__nav" data-bmi-moves-footer>
                <button type="button" class="fc-bmi__navBtn fc-bmi__navBtn--undo"
                        data-bmi-undo
                        aria-label="Undo last move"
                        title="Undo last move"
                        disabled>
                    &#8592;
                </button>
                <span class="fc-bmi__hint">Click or drag pieces · &#8592; to undo</span>
            </div>
            ${this._enableModeSelector ? `
            <div class="fc-bmi__editor" data-bmi-editor hidden>
                ${['w', 'b'].map(color => `
                <div class="fc-bmi__palette" role="group" aria-label="${color === 'w' ? 'White' : 'Black'} pieces">
                    ${EDITOR_PIECES.map(([type, name]) => `
                    <button type="button" class="fc-bmi__pieceBtn" data-bmi-tool="${color}${type}"
                            aria-pressed="false" aria-label="Place ${color === 'w' ? 'White' : 'Black'} ${name}"
                            title="Place ${color === 'w' ? 'White' : 'Black'} ${name}">
                        <svg viewBox="0 0 40 40" aria-hidden="true" focusable="false"><use href="${ASSETS_URL}pieces/standard.svg#${color}${type}"></use></svg>
                    </button>`).join('')}
                </div>`).join('')}
                <div class="fc-bmi__editorTools" role="group" aria-label="Editing tool">
                    <button type="button" class="fc-bmi__editorBtn" data-bmi-tool="reposition"
                            aria-pressed="true" title="Reposition pieces freely by dragging or selecting two squares">Reposition</button>
                    <button type="button" class="fc-bmi__editorBtn" data-bmi-tool="remove"
                            aria-pressed="false" title="Select Remove, then click a square">Remove</button>
                </div>
                <div class="fc-bmi__editorTools" role="group" aria-label="Editor controls">
                    <button type="button" class="fc-bmi__editorBtn" data-bmi-reset
                            aria-label="Reset to starting position" title="Reset to starting position">Reset</button>
                    <button type="button" class="fc-bmi__editorBtn" data-bmi-clear
                            aria-label="Clear all pieces" title="Clear all pieces">Clear</button>
                    <button type="button" class="fc-bmi__editorBtn" data-bmi-flip
                            aria-label="Flip board orientation" title="Flip board orientation">Flip</button>
                </div>
                <span class="fc-bmi__editorHint">Choose a piece or Remove, then click a square.</span>
                <div class="fc-bmi__editorTools" role="group" aria-label="Side to move">
                    <span class="fc-bmi__editorLabel">To move</span>
                    <button type="button" class="fc-bmi__editorBtn" data-bmi-side="w" aria-pressed="true" title="White to move">White</button>
                    <button type="button" class="fc-bmi__editorBtn" data-bmi-side="b" aria-pressed="false" title="Black to move">Black</button>
                </div>
                <label class="fc-bmi__fenLabel">FEN
                    <input class="fc-bmi__fenInput" data-bmi-fen aria-label="Position FEN" autocomplete="off" spellcheck="false">
                </label>
                <div class="fc-bmi__validation" data-bmi-validation role="status" aria-live="polite"></div>
                <details class="fc-bmi__positionDetails" data-bmi-details>
                    <summary>Position details</summary>
                    <div class="fc-bmi__castling" role="group" aria-label="Castling rights">
                        <span>Castling</span>
                        ${[['K', 'White kingside'], ['Q', 'White queenside'], ['k', 'Black kingside'], ['q', 'Black queenside']].map(([right, label]) => `
                        <label><input type="checkbox" data-bmi-castling="${right}" aria-label="${label} castling" title="${label} castling">${right}</label>`).join('')}
                    </div>
                    <label class="fc-bmi__editorLabel">En passant
                        <select class="fc-bmi__epInput" data-bmi-ep aria-label="En-passant target">
                            <option value="-">None</option>
                            ${['3', '6'].map(rank => Array.from('abcdefgh', file => `<option value="${file}${rank}">${file}${rank}</option>`).join('')).join('')}
                        </select>
                    </label>
                </details>
                <div class="fc-bmi__searchMessage" data-bmi-search-message hidden role="alert">
                    <strong data-bmi-search-title></strong>
                    <p data-bmi-search-body></p>
                    <button type="button" class="fc-bmi__editorBtn" data-bmi-continue>Continue Editing</button>
                </div>
                <div class="fc-bmi__editorTools">
                    <button type="button" class="fc-bmi__editorBtn" data-bmi-search hidden disabled
                            aria-disabled="true" aria-label="Search Position"
                            title="Search for games containing this position.">Search Position</button>
                </div>
            </div>` : ''}
        `;

        // ── Positioning ────────────────────────────────────────────────────
        // Append to document.body with fixed positioning so the panel sits above
        // all other content regardless of the search bar's stacking context.
        document.body.appendChild(panel);
        this._positionPanel(panel, anchorEl);

        this._panelEl = panel;
        this._boardEl = panel.querySelector('[data-bmi-board]');
        this._undoBtn = panel.querySelector('[data-bmi-undo]');

        this._undoBtn.addEventListener('click', () => this.undo());
        panel.querySelectorAll('[data-bmi-mode]').forEach(button => {
            button.addEventListener('click', () => this._setMode(button.dataset.bmiMode));
        });
        panel.querySelectorAll('[data-bmi-tool]').forEach(button => {
            button.addEventListener('click', () => this._setEditorTool(button.dataset.bmiTool));
        });
        panel.querySelector('[data-bmi-continue]')?.addEventListener('click', () => {
            panel.querySelector('[data-bmi-search-message]').hidden = true;
            panel.querySelector('[data-bmi-fen]').focus();
        });
        panel.querySelector('[data-bmi-search]')?.addEventListener('click', () => {
            if (this._mode === 'board-editor' && !panel.querySelector('[data-bmi-search]').disabled) this.submit();
        });
        panel.querySelector('[data-bmi-reset]')?.addEventListener('click', () => this._resetEditor());
        panel.querySelector('[data-bmi-clear]')?.addEventListener('click', () => this._clearEditor());
        panel.querySelector('[data-bmi-flip]')?.addEventListener('click', () => this._flipEditor());
        panel.querySelector('[data-bmi-fen]')?.addEventListener('input', event => this._loadEditorFen(event.target.value));
        panel.querySelectorAll('[data-bmi-side]').forEach(button => {
            button.addEventListener('click', () => {
                this._editorMetadata.side = button.dataset.bmiSide;
                this._editorMetadata.ep = '-';
                this._syncEditorFen();
            });
        });
        panel.querySelectorAll('[data-bmi-castling]').forEach(input => {
            input.addEventListener('change', () => {
                this._editorMetadata.castling = Array.from(panel.querySelectorAll('[data-bmi-castling]'))
                    .filter(item => item.checked).map(item => item.dataset.bmiCastling).join('') || '-';
                this._syncEditorFen();
            });
        });
        panel.querySelector('[data-bmi-ep]')?.addEventListener('change', event => {
            this._editorMetadata.ep = event.target.value;
            this._syncEditorFen();
        });
        panel.querySelector('[data-bmi-details]')?.addEventListener('toggle', () => {
            this._positionPanel(panel, anchorEl, { keepAnchor: true });
        });
    }

    _setMode(mode) {
        if (!this._enableModeSelector || mode === this._mode
            || (mode !== 'moves' && mode !== 'board-editor')) return;
        this._inputGeneration++;
        this._board?.disableMoveInput();
        this._clearSelectedMarker();
        this._suppressNextDestinationClick = false;
        this._mode = mode;
        this._onModeChange?.(mode);
        this._panelEl.setAttribute('aria-label', mode === 'board-editor'
            ? 'Edit chess position' : 'Enter moves on chessboard');
        this._panelEl?.querySelectorAll('[data-bmi-mode]').forEach(button => {
            button.setAttribute('aria-pressed', String(button.dataset.bmiMode === mode));
        });
        this._panelEl.querySelector('[data-bmi-moves-footer]').hidden = mode !== 'moves';
        this._panelEl.querySelector('[data-bmi-editor]').hidden = mode !== 'board-editor';
        this._boardEl.tabIndex = mode === 'board-editor' ? 0 : -1;
        this._boardEl.setAttribute('role', 'group');
        if (mode === 'board-editor') {
            this._resetEditor();
        } else {
            this._editorPosition = null;
            this._boardEl.setAttribute('aria-label', 'Chessboard');
            this._setBoardOrientation(COLOR.white);
            this._syncBoard();
        }
        this._syncEditorSearch();
        this._positionPanel(this._panelEl, this._anchorEl);
    }

    _resetEditor() {
        if (this._mode !== 'board-editor') return;
        const fen = this._editorResetFen || FEN.start;
        const [placement, side, castling, ep, halfmove, fullmove] = fen.split(' ');
        this._editorPosition = new Position(placement);
        this._editorCleared = false;
        this._editorMetadata = { side, castling, ep, halfmove, fullmove };
        this._captureEditorCastlingHomes();
        const reset = this._panelEl.querySelector('[data-bmi-reset]');
        const label = fen === FEN.start ? 'Reset to starting position' : 'Reset to original editor position';
        reset.setAttribute('aria-label', label);
        reset.title = label;
        this._editorOrientation = COLOR.white;
        this._editorFocusSquare = 'a1';
        this._updateEditorFocusLabel();
        this._setBoardOrientation(COLOR.white);
        this._board?.removeMarkers(MARKER_LAST_FROM);
        this._board?.removeMarkers(MARKER_LAST_TO);
        this._setEditorTool('reposition');
        this._syncEditorBoard();
    }

    _clearEditor() {
        if (this._mode !== 'board-editor') return;
        this._editorPosition = new Position(FEN.empty);
        this._editorCleared = true;
        this._editorMetadata.castling = '-';
        this._editorMetadata.ep = '-';
        this._clearSelectedMarker();
        this._syncEditorBoard();
    }

    _flipEditor() {
        if (this._mode !== 'board-editor' || !this._board) return;
        this._clearSelectedMarker();
        this._editorOrientation = this._editorOrientation === COLOR.white ? COLOR.black : COLOR.white;
        this._setBoardOrientation(this._editorOrientation);
    }

    _setBoardOrientation(color) {
        const board = this._board;
        if (!board) return;
        // The library ignores a second turn while one is queued. Serialize
        // requests so quick Flip/Reset/mode changes always reach the last state.
        this._orientationTask = (this._orientationTask || Promise.resolve()).then(() => {
            if (this._board === board) return board.setOrientation(color, false);
        });
    }

    _setEditorTool(tool) {
        if (this._mode !== 'board-editor') return;
        this._editorTool = tool;
        this._clearSelectedMarker();
        this._suppressNextDestinationClick = false;
        this._panelEl.querySelectorAll('[data-bmi-tool]').forEach(button => {
            button.setAttribute('aria-pressed', String(button.dataset.bmiTool === tool));
        });
        if (this._board) {
            this._board.disableMoveInput();
            this._enableEditorInput();
        }
    }

    _captureEditorCastlingHomes() {
        this._editorCastlingHomes = {};
        if (this._editorContext?.variant !== 'chess960') return;
        for (const right of this._editorMetadata.castling.replace('-', '')) {
            const white = right === right.toUpperCase(), color = white ? 'w' : 'b', rank = white ? '1' : '8';
            const king = Array.from('abcdefgh', file => file + rank).find(square => this._editorPosition.getPiece(square) === color + 'k');
            if (!king) continue;
            let file = right.toLowerCase();
            if ('KQkq'.includes(right)) {
                const rooks = Array.from('abcdefgh').filter(f => this._editorPosition.getPiece(f + rank) === color + 'r');
                file = right.toLowerCase() === 'k' ? rooks.filter(f => f > king[0]).at(-1) : rooks.find(f => f < king[0]);
            }
            if (file) this._editorCastlingHomes[right] = [king, color + 'k', file + rank, color + 'r'];
        }
    }

    _pruneEditorMetadata() {
        if (this._editorPosition.getPieces().length > 0) this._editorCleared = false;
        // Arbitrary editing supplies no move history. Remove unavailable
        // rights; never infer new rights or an en-passant target.
        const homes = { K: ['e1', 'wk', 'h1', 'wr'], Q: ['e1', 'wk', 'a1', 'wr'],
            k: ['e8', 'bk', 'h8', 'br'], q: ['e8', 'bk', 'a8', 'br'] };
        this._editorMetadata.castling = Array.from(this._editorMetadata.castling).filter(right => {
            const home = this._editorContext?.variant === 'chess960' ? this._editorCastlingHomes[right] : homes[right];
            return home && this._editorPosition.getPiece(home[0]) === home[1]
                && this._editorPosition.getPiece(home[2]) === home[3];
        }).join('') || '-';
        this._editorMetadata.ep = '-';
    }

    _syncEditorBoard({ placementEdited = false } = {}) {
        if (this._mode !== 'board-editor' || !this._editorPosition) return;
        if (placementEdited) this._pruneEditorMetadata();
        this._clearSelectedMarker();
        this._board?.setPosition(this._editorPosition.getFen(), false);
        this._syncEditorFen();
    }

    getEditorState() {
        if (this._mode !== 'board-editor' || !this._editorPosition) return null;
        const m = this._editorMetadata;
        const fen = this._editorCleared ? ''
            : `${this._editorPosition.getFen()} ${m.side} ${m.castling} ${m.ep} ${m.halfmove} ${m.fullmove}`;
        const result = this._editorCleared
            ? { ok: false, error: 'Position incomplete · Add exactly one king for each side.' }
            : validatePositionFen(fen, this._editorContext);
        let message = this._fenError || (result.ok ? '' : result.error);
        if (!this._fenError && /king/.test(message || '')) message = 'Position incomplete · Add exactly one king for each side.';
        return { ...this._editorContext, fen, side: m.side, searchable: !message, message: message || '' };
    }

    _syncEditorFen({ keepInput = false } = {}) {
        if (this._mode !== 'board-editor' || !this._panelEl) return;
        if (!keepInput) this._fenError = null;
        const state = this.getEditorState();
        const input = this._panelEl.querySelector('[data-bmi-fen]');
        if (!keepInput) input.value = state.fen;
        input.setAttribute('aria-invalid', String(!state.searchable));
        input.setAttribute('aria-label', state.variant === 'chess960' ? `Chess960 position FEN, SPID ${state.spid}` : 'Position FEN');
        const reposition = this._panelEl.querySelector('[data-bmi-tool="reposition"]');
        if (reposition) reposition.title = state.variant === 'chess960'
            ? 'Reposition pieces freely. Set To move manually for Chess960.' : 'Reposition pieces';
        this._panelEl.querySelector('[data-bmi-validation]').textContent = state.message;
        this._panelEl.querySelectorAll('[data-bmi-side]').forEach(button => {
            button.setAttribute('aria-pressed', String(button.dataset.bmiSide === state.side));
        });
        this._panelEl.querySelectorAll('[data-bmi-castling]').forEach(input => {
            input.checked = this._editorMetadata.castling.includes(input.dataset.bmiCastling);
            const chess960 = this._editorContext?.variant === 'chess960';
            input.disabled = chess960;
            if (input.parentElement) input.parentElement.hidden = chess960;
        });
        const castlingGroup = this._panelEl.querySelector('.fc-bmi__castling');
        if (castlingGroup) {
            let preserved = castlingGroup.querySelector('[data-bmi-castling-preserved]');
            if (!preserved) {
                preserved = document.createElement('span');
                preserved.dataset.bmiCastlingPreserved = '';
                castlingGroup.appendChild(preserved);
            }
            preserved.hidden = this._editorContext?.variant !== 'chess960';
            preserved.textContent = `${this._editorMetadata.castling} · Edit rights in FEN`;
        }
        this._panelEl.querySelector('[data-bmi-ep]').value = this._editorMetadata.ep;
        this._onEditorChange?.(state);
        this._syncEditorSearch();
        this._positionPanel(this._panelEl, this._anchorEl, { keepAnchor: true });
    }

    _loadEditorFen(value) {
        if (this._mode !== 'board-editor') return;
        let fen = String(value || '').trim().replace(/\s+/g, ' ');
        // The index accepts four-field keys; supply the library's usual clocks.
        if (fen.split(' ').length === 4) fen += ' 0 1';
        const result = validatePositionFen(fen, this._editorContext);
        if (!result.ok) {
            this._fenError = result.error || 'Invalid FEN';
            this._syncEditorFen({ keepInput: true });
            return;
        }
        const [placement, side, castling, ep, halfmove, fullmove] = fen.split(' ');
        this._editorPosition = new Position(placement);
        this._editorCleared = false;
        this._editorMetadata = { side, castling, ep, halfmove, fullmove };
        this._captureEditorCastlingHomes();
        this._inputGeneration++;
        this._setEditorTool('reposition');
        this._syncEditorBoard();
    }

    _repositionEditor(from, to) {
        if (from === to) return;
        const state = this.getEditorState();
        if (this._editorContext?.variant === 'standard' && state?.fen && this._editorPosition.getPiece(from)?.[0] === state.side) {
            try {
                const chess = new Chess(state.fen);
                // Same queen-promotion default as legal entry in Moves mode.
                const move = chess.move({ from, to, promotion: 'q' });
                if (move) {
                    // Preserve raw EP before applying the existing index identity's
                    // adjacency normalization (including pinned adjacent pawns).
                    const fields = chess.fen({ forceEnpassantSquare: true }).split(' ');
                    const identity = positionIdentity(fields.join(' '));
                    if (!identity) throw new Error('Result is not a searchable chess position');
                    const [placement, side, castling, ep] = identity.split(' ');
                    this._editorPosition = new Position(placement);
                    this._editorMetadata = { side, castling, ep, halfmove: fields[4], fullmove: fields[5] };
                    this._editorCleared = false;
                    return;
                }
            } catch (_) { /* Incomplete/illegal positions retain unrestricted editing. */ }
        }
        this._editorPosition.movePiece(from, to);
        this._pruneEditorMetadata();
    }

    _editSquare(square) {
        if (!this._editorPosition || !/^[a-h][1-8]$/.test(square)) return;
        if (this._editorTool === 'reposition') {
            if (this._selectedSquare) {
                if (this._selectedSquare !== square) this._repositionEditor(this._selectedSquare, square);
                this._syncEditorBoard();
            } else if (this._editorPosition.getPiece(square)) {
                this._markSelectedSquare(square);
            }
        } else {
            this._editorPosition.setPiece(square, this._editorTool === 'remove' ? null : this._editorTool);
            this._syncEditorBoard({ placementEdited: true });
        }
    }

    _enableEditorInput() {
        if (!this._board || this._editorTool !== 'reposition') return;
        const generation = this._inputGeneration;
        let pending = false;
        this._board.enableMoveInput(event => {
            if (this._mode !== 'board-editor' || generation !== this._inputGeneration) return false;
            if (event.type === INPUT_EVENT_TYPE.moveInputStarted) {
                if (pending) return false;
                this._suppressNextDestinationClick = true;
                return true;
            }
            if (event.type === INPUT_EVENT_TYPE.validateMoveInput) {
                this._suppressNextDestinationClick = true;
                pending = true;
                this._repositionEditor(event.squareFrom, event.squareTo);
                this._syncEditorFen();
                requestAnimationFrame(() => {
                    if (this._mode === 'board-editor' && generation === this._inputGeneration) this._syncEditorBoard();
                    pending = false;
                });
                // We apply placement ourselves. cm-chessboard's click workflow
                // does not move onto a same-color piece when validation returns
                // true; declining its automatic move lets it finish/reset input.
                return false;
            }
        }); // No color restriction: both sides can be relocated.
    }

    _updateEditorFocusLabel() {
        this._boardEl?.setAttribute('aria-label', `Position editor, square ${this._editorFocusSquare}. Arrow keys select a square; Enter edits; Delete removes a piece.`);
    }

    _onEditorKeyDown(e) {
        if (this._mode !== 'board-editor' || !this._board) return;
        const directions = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, 1], ArrowDown: [0, -1] };
        if (directions[e.key]) {
            e.preventDefault();
            const sign = this._editorOrientation === COLOR.white ? 1 : -1;
            const [dx, dy] = directions[e.key];
            const file = Math.max(97, Math.min(104, this._editorFocusSquare.charCodeAt(0) + dx * sign));
            const rank = Math.max(1, Math.min(8, Number(this._editorFocusSquare[1]) + dy * sign));
            this._editorFocusSquare = `${String.fromCharCode(file)}${rank}`;
            this._board.removeMarkers(MARKER_LAST_TO);
            this._board.addMarker(MARKER_LAST_TO, this._editorFocusSquare);
            this._updateEditorFocusLabel();
        } else if (e.key === 'Enter' || e.key === ' ' || e.key === 'Delete' || e.key === 'Backspace') {
            e.preventDefault();
            e.stopPropagation();
            if (e.key === 'Delete' || e.key === 'Backspace') {
                this._editorPosition.setPiece(this._editorFocusSquare, null);
                this._syncEditorBoard({ placementEdited: true });
            } else this._editSquare(this._editorFocusSquare);
        }
    }

    /**
     * Prefer below the anchor, then above, clamping to the viewport edges.
     * CSS limits panel dimensions; short viewports can scroll the panel.
     */
    _positionPanel(panel, anchorEl, { keepAnchor = false } = {}) {
        const rect = anchorEl.getBoundingClientRect();

        panel.style.position = 'fixed';
        // Layout dimensions exclude transforms from the opening animation.
        const bounds = panel.getBoundingClientRect();
        const width = panel.offsetWidth || bounds.width;
        const height = panel.offsetHeight || bounds.height;
        const margin = 8;
        // GA aligns the panel's right edge with its toolbar button.
        const preferredLeft = keepAnchor ? Number.parseFloat(panel.style.left)
            : this._enableModeSelector ? rect.right - width : rect.left;
        const left = Math.min(preferredLeft, window.innerWidth - width - margin);
        let top = keepAnchor ? Number.parseFloat(panel.style.top) : rect.bottom + margin;
        if (!keepAnchor && top + height > window.innerHeight - margin) {
            top = rect.top - height - margin;
        }
        top = Math.max(margin, Math.min(top, window.innerHeight - height - margin));

        panel.style.top  = `${top}px`;
        panel.style.left = `${Math.max(8, left)}px`;
    }

    // ── Private: cm-chessboard ────────────────────────────────────────────────

    _createBoard() {
        if (!this._boardEl || !this._chess) return;

        this._board = new Chessboard(this._boardEl, {
            position: this._mode === 'board-editor' ? this._editorPosition.getFen() : this._chess.fen(),
            assetsUrl: ASSETS_URL,
            style: {
                animationDuration: 300
            },
            // The drag helper is rendered as a separate body-level SVG. Use
            // direct sprite URLs so the helper can resolve piece symbols outside
            // the board SVG/cache wrapper.
            assetsCache: false,
            extensions: [{ class: Markers }]
            // No style overrides — cm-chessboard defaults give us the native
            // from-square highlight and legal-move dots on piece selection.
        });
        this._boardEl.addEventListener('click', this._boundBoardClick);
        this._boardEl.addEventListener('keydown', this._boundBoardKeyDown);
    }

    /**
     * Enable piece input for whichever side is to move.
     * Must be called after every move and after every undo so the correct
     * color's pieces become draggable/clickable.
     *
     * The handler deliberately does NOT call setPosition() from inside
     * validateMoveInput — instead it schedules it in a rAF so cm-chessboard
     * can finish its own piece animation first. This prevents edge cases where
     * en-passant captures or castling leave the board out of sync.
     */
    _enableMoveInput() {
        if (!this._board || !this._chess) return;
        if (this._mode === 'board-editor') {
            this._enableEditorInput();
            return;
        }
        const generation = this._inputGeneration;

        // If the game is over, leave the board in display-only mode
        if (this._chess.isGameOver()) {
            this._board.disableMoveInput();
            return;
        }

        const color = this._chess.turn() === 'w' ? COLOR.white : COLOR.black;

        this._board.enableMoveInput((event) => {
            if (this._mode !== 'moves' || generation !== this._inputGeneration) return false;
            switch (event.type) {

                case INPUT_EVENT_TYPE.moveInputStarted:
                    // Always allow the player to pick up any piece of the side to move
                    this._suppressNextDestinationClick = true;
                    this._markSelectedSquare(event.squareFrom);
                    return true;

                case INPUT_EVENT_TYPE.validateMoveInput: {
                    this._suppressNextDestinationClick = true;
                    let move;
                    try {
                        move = this._chess.move({
                            from:      event.squareFrom,
                            to:        event.squareTo,
                            promotion: 'q',  // always promote to queen (simplest for move entry)
                        });
                    } catch (_) {
                        // chess.js throws for illegal input. Reject it normally
                        // so cm-chessboard can finish input and restore the piece.
                        return false;
                    }

                    if (!move) {
                        // Illegal move — snap piece back
                        return false;
                    }

                    this._clearSelectedMarker();

                    // Legal move confirmed. Schedule board sync + re-enable for next side.
                    requestAnimationFrame(() => {
                        if (!this._board || !this._chess || this._mode !== 'moves'
                            || generation !== this._inputGeneration) return;
                        this._board.setPosition(this._chess.fen(), true);
                        this._soundController?.playFromMove(move);
                        // Must disable before re-enabling — cm-chessboard throws
                        // "moveInput already enabled" if enableMoveInput() is called
                        // while input is still active from the previous side's turn.
                        this._board.disableMoveInput();
                        this._enableMoveInput();
                        this._updateUndoBtn();
                        this._highlightLastMove();

                    });

                    // Search ownership follows the accepted chess state immediately.
                    // Only rendering waits: switching/closing before rAF must not lose a move.
                    this._onMovesChange?.(this._getMoveSequence());

                    return true;
                }

                case INPUT_EVENT_TYPE.moveInputFinished:
                    this._clearSelectedMarker();
                    break;
                case INPUT_EVENT_TYPE.moveInputCanceled:
                    this._clearSelectedMarker();
                    break;
            }
        }, color);
    }

    _markSelectedSquare(square) {
        if (!this._board || !square) return;
        this._selectedSquare = square;
        this._board.removeMarkers(MARKER_SELECTED);
        this._board.addMarker(MARKER_SELECTED, square);
    }

    _clearSelectedMarker() {
        this._selectedSquare = null;
        if (!this._board) return;
        this._board.removeMarkers(MARKER_SELECTED);
    }

    _onBoardClick(e) {
        if (!this._board || !this._chess || !this._boardEl) return;

        if (this._mode === 'board-editor') {
            if (this._suppressNextDestinationClick) {
                this._suppressNextDestinationClick = false;
                return;
            }
            const squareEl = e.target?.closest('[data-square]');
            if (squareEl && this._boardEl.contains(squareEl)) this._editSquare(squareEl.getAttribute('data-square'));
            return;
        }

        if (this._suppressNextDestinationClick) {
            this._suppressNextDestinationClick = false;
            return;
        }

        if (this._selectedSquare) return;

        const squareEl = e.target && e.target.closest
            ? e.target.closest('[data-square]')
            : null;
        if (!squareEl || !this._boardEl.contains(squareEl)) return;

        const square = squareEl.getAttribute('data-square');
        if (!square) return;

        const move = this._findUniqueDestinationMove(square);
        if (!move) return;

        this._playDestinationMove(move);
    }

    _findUniqueDestinationMove(square) {
        const moves = this._chess.moves({ verbose: true })
            .filter(move => move.to === square);

        if (moves.length === 1) {
            return moves[0];
        }

        const promotionMoves = moves.filter(move => !!move.promotion);
        if (promotionMoves.length > 1 && promotionMoves.length === moves.length) {
            const queenPromotion = promotionMoves.filter(move => move.promotion === 'q');
            const sameMove = promotionMoves.every(move =>
                move.from === promotionMoves[0].from && move.to === promotionMoves[0].to);
            // TODO: Replace this queen default with a promotion picker when
            // BoardMoveInput gets promotion UI.
            if (sameMove && queenPromotion.length === 1) {
                return queenPromotion[0];
            }
        }

        return null;
    }

    _playDestinationMove(move) {
        if (!move || !this._chess) return;

        const result = this._chess.move({
            from: move.from,
            to: move.to,
            promotion: move.promotion || (String(move.flags || '').includes('p') ? 'q' : undefined)
        });
        if (!result) return;

        this._syncBoard(true);
        this._soundController?.playFromMove(result);
        if (this._onMovesChange) {
            this._onMovesChange(this._getMoveSequence());
        }
    }

    /** Add last-move markers to the two squares involved in the latest half-move. */
    _highlightLastMove() {
        if (!this._board || !this._chess) return;

        this._board.removeMarkers(MARKER_LAST_FROM);
        this._board.removeMarkers(MARKER_LAST_TO);

        const history = this._chess.history({ verbose: true });
        if (!history.length) return;

        const last = history[history.length - 1];
        this._board.addMarker(MARKER_LAST_FROM, last.from);
        this._board.addMarker(MARKER_LAST_TO,   last.to);
    }

    _syncBoard(animated = false) {
        if (!this._board || !this._chess) return;
        this._clearSelectedMarker();
        this._board.disableMoveInput();
        this._board.setPosition(this._chess.fen(), animated);
        this._enableMoveInput();
        this._updateUndoBtn();
        this._highlightLastMove();
    }

    // ── Private: formatting ───────────────────────────────────────────────────

    /**
     * Returns the full move sequence in the format the search servlet expects:
     *   "1.e4 c5 2.Nf3 d6"
     * Returns an empty string if no moves have been played.
     */
    _getMoveSequence() {
        const history = this._chess ? this._chess.history() : [];
        if (!history.length) return '';

        const parts = [];
        for (let i = 0; i < history.length; i++) {
            if (i % 2 === 0) {
                // White's move — prefix with move number
                parts.push(`${Math.floor(i / 2) + 1}.${history[i]}`);
            } else {
                // Black's move — no prefix, space-separated
                parts.push(history[i]);
            }
        }
        return parts.join(' ');
    }

    // ── Private: state helpers ────────────────────────────────────────────────

    _updateUndoBtn() {
        if (!this._undoBtn || !this._chess) return;
        const hasHistory = this._chess.history().length > 0;
        this._undoBtn.disabled = !hasHistory;
        this._undoBtn.setAttribute('aria-disabled', String(!hasHistory));
    }

    // ── Private: event handlers ───────────────────────────────────────────────

    /** Dismiss on click outside the panel. */
    _onOutsideClick(e) {
        if (this._panelEl && !this._panelEl.contains(e.target)) {
            if (this._mode === 'board-editor' && this._isSearchTarget?.(e.target)) {
                this.submit();
                return;
            }
            this.cancel();
        }
    }

    /** Escape → cancel, Enter → submit (when panel has focus). */
    _onKeyDown(e) {
        if (!this._panelEl) return;

        if (e.key === 'Escape') {
            e.preventDefault();
            this.cancel();
        } else if (e.key === 'Enter') {
            // Let native button activation switch modes without submitting a search.
            if (e.target?.closest('[data-bmi-mode]')
                || (this._mode === 'board-editor' && this._panelEl.contains(e.target))) return;
            e.preventDefault();
            this.submit();
        }
    }
}
